<template>
  <v-row>
    <v-col cols="auto" md="6" sm="12">
      <v-menu
        v-model="startMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        min-width="290px"
      >
        <template v-slot:activator="{ props }">
          <v-text-field
            :model-value="displayDate(innerValue.startDateTime)"
            :label="innerLabels.start"
            :disabled="isDisabled"
            prepend-icon="event"
            readonly
            variant="outlined"
            density="compact"
            color="primary"
            :rules="[isDateRangeValid]"
            :error-messages="errors"
            name="start date"
            ref="startPicker"
            v-bind="props"
          />
        </template>
        <v-date-picker
          :model-value="toJsDate(innerValue.startDateTime)"
          @update:model-value="onPick('startDateTime', $event)"
          color="primary"
          name="start date"
        />
        <v-btn color="primary" @click="startMenu = false"> Close </v-btn>
      </v-menu>
    </v-col>

    <v-col cols="auto" md="6" sm="12">
      <v-menu
        v-model="endMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        min-width="290px"
      >
        <template v-slot:activator="{ props }">
          <v-text-field
            :model-value="displayDate(innerValue.endDateTime)"
            :label="innerLabels.end"
            :disabled="isDisabled"
            prepend-icon="event"
            readonly
            variant="outlined"
            density="compact"
            color="primary"
            :rules="[isDateRangeValid]"
            name="end date"
            ref="endPicker"
            v-bind="props"
          />
        </template>
        <v-date-picker
          :model-value="toJsDate(innerValue.endDateTime)"
          @update:model-value="onPick('endDateTime', $event)"
          color="primary"
          name="end date"
        />
        <v-btn color="primary" @click="endMenu = false"> Close </v-btn>
      </v-menu>
    </v-col>
  </v-row>
</template>

<script>
import { DateTime } from 'luxon';

export default {
  props: {
    value: {
      type: [Object, String],
      default: () => {
        return {
          startDateTime: DateTime.local(),
          endDateTime: DateTime.local(),
        };
      },
    },
    // Vue 3 `v-model` binds `modelValue`; several call sites use v-model
    // instead of `:value`. Support both so the initial range flows in and
    // selections flow back out (`update:modelValue`).
    modelValue: {
      type: [Object, String],
      default: undefined,
    },
    dateFormat: {
      type: String,
      default: 'yyyy-MM-dd',
    },
    debounce: {
      type: Number,
      default: 100,
    },
    labels: {
      type: [Object, String],
      default: () => {
        return {
          start: 'Start Date',
          end: 'End Date',
        };
      },
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      innerValue: {
        startDateTime: '',
        endDateTime: '',
      },
      startMenu: false,
      endMenu: false,
      innerDateFormat: '',
      innerLabels: {},
      pendingChange: false,
      errors: [],
    };
  },

  created() {
    // Prefer the v-model source (`modelValue`) when provided, otherwise the
    // legacy `:value` prop.
    const incoming = this.modelValue !== undefined ? this.modelValue : this.value;
    if (incoming) {
      this.innerValue = incoming;
    }

    if (this.labels) {
      this.innerLabels = this.labels;
    }

    if (this.dateFormat) {
      this.innerDateFormat = this.dateFormat;
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.innerValue = this.formatDates(newVal);
      }
    },

    modelValue(newVal) {
      if (newVal) {
        this.innerValue = this.formatDates(newVal);
      }
    },

    dateFormat(newVal) {
      if (newVal) {
        this.innerDateFormat = newVal;
      }
    },

    labels(newVal) {
      if (newVal) {
        this.innerLabels = newVal;
      }
    },

    pendingChange(newVal) {
      if (newVal === false) {
        this.$emit('change', this.innerValue);
      }
    },
  },

  methods: {
    // Parse any incoming value (yyyy-MM-dd string, ISO datetime, luxon
    // DateTime, or Date) into a Date anchored to LOCAL midnight. Parsing a
    // "yyyy-MM-dd" string with `new Date()` treats it as UTC, which renders as
    // the previous day in timezones behind UTC — that was the off-by-one/off-
    // by-year display bug. Building the Date from the date parts avoids it.
    parseToLocalDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === 'object' && typeof value.toJSDate === 'function') {
        return value.toJSDate();
      }
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    },

    // v-date-picker works with native Date; feed it a local-midnight Date so
    // it highlights the correct day.
    toJsDate(value) {
      return this.parseToLocalDate(value);
    },

    // Format for the read-only text field as MM/DD/YYYY, using local date
    // parts so the displayed day matches the stored value.
    displayDate(value) {
      const date = this.parseToLocalDate(value);
      if (!date) return '';
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${mm}/${dd}/${date.getFullYear()}`;
    },

    // Normalize a picked Date back to a local yyyy-MM-dd string (NOT
    // toISOString, which converts to UTC and can shift the day).
    toDateString(value) {
      const date = this.parseToLocalDate(value);
      if (!date) return '';
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${mm}-${dd}`;
    },

    // Explicitly propagate each selection. Relying on a deep watcher over
    // innerValue was unreliable, so emit the change directly here. innerValue
    // is kept as yyyy-MM-dd strings so display and emitted values stay
    // consistent and timezone-safe.
    onPick(field, value) {
      this.innerValue = { ...this.innerValue, [field]: this.toDateString(value) };
      if (field === 'startDateTime') this.startMenu = false;
      else this.endMenu = false;
      // date-change: legacy explicit handler; update:modelValue: v-model.
      this.$emit('date-change', this.innerValue);
      this.$emit('update:modelValue', this.innerValue);
    },
    debounceHandler(func, delay) {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(this, args), delay);
      };
    },
    // changeHandler(propName, e) {
    //   const adapter = useDate();
    //   this.innerValue[propName] = adapter.date(e.$d);
    //   // this.innerValue[propName] = DateTime.toISODate();
    //   console.log('evetn', e);
    //   console.log('changeHandler', propName);
    //   this.pendingChange = true;
    // },

    defaultDates() {
      this.innerValue = {
        startDateTime: DateTime.fromFormat(
          DateTime.local(),
          this.innerDateFormat
        ),
        endDateTime: DateTime.fromFormat(
          DateTime.local(),
          this.innerDateFormat
        ),
      };
    },

    formatDates(input) {
      if (input.startDateTime && input.endDateTime) {
        const sdt = DateTime.fromISO(input.startDateTime).toFormat(
          this.innerDateFormat
        );
        const edt = DateTime.fromISO(input.endDateTime).toFormat(
          this.innerDateFormat
        );
        input.startDateTime = sdt;
        input.endDateTime = edt;
      }

      return input;
    },

    isDateRangeValid() {
      if (this.innerValue.startDateTime > this.innerValue.endDateTime) {
        this.errors.push('Start Date must be less than End Date.');
        return false;
      } else {
        this.errors = [];
        return true;
      }
    },
  },
};
</script>
